import * as React from "react";
import { useContext, useRef, useMemo } from "react";
import * as Immutable from "immutable";
import { isInfiniteScrollUp } from "logview/helpers";
import { loadPrevPage, loadNextPage } from "./LogViewDataFetching";
import ListStateContext from "./ListStateContext";
import ScrollPositionContext from "./ScrollPositionContext";
const _getVisiblePagesWithMessages = (visiblePagesIds, loadedPages) => {
  let visiblePages = Immutable.OrderedSet();
  visiblePagesIds.sort((pageIdA, pageIdB) => pageIdB - pageIdA).forEach((pageId) => {
    visiblePages = visiblePages.add([pageId, loadedPages[pageId]]);
  });
  return visiblePages;
};
const ListStateProvider = ({
  children,
  pageRefs,
  after,
  listState,
  tableRef,
  updateLogViewState,
  onLoadMessages,
  infiniteScrollDirection,
  loadedAllMessages
}) => {
  const {
    finishedScrollPositionUpdateRef,
    setFinishedScrollPositionUpdate,
    setLastPosition: setLastScrollPosition
  } = useContext(ScrollPositionContext);
  const pages = _getVisiblePagesWithMessages(listState.visiblePagesIds, listState.loadedPages);
  const _isInfiniteScrollUp = isInfiniteScrollUp(infiniteScrollDirection);
  const bottomPageId = _isInfiniteScrollUp ? listState.visiblePagesIds.min() : listState.visiblePagesIds.max();
  const loadedAllPrevMessages = loadedAllMessages(listState.loadedMessagesCount, after, bottomPageId);
  const loadPrevPromiseRef = useRef(void 0);
  const listStateContextValue = useMemo(() => {
    const updateListState = (newInternalListState, scrollPositionUpdate) => {
      setFinishedScrollPositionUpdate(false);
      setLastScrollPosition(tableRef.current.scrollTop);
      updateLogViewState({ listState: newInternalListState, scrollPositionUpdate });
    };
    const _loadPrevPage = () => loadPrevPage({
      finishedScrollPositionUpdateRef,
      internalListState: listState,
      loadedAllPrevMessages,
      loadPrevPromiseRef,
      pageRefs,
      updateListState,
      onLoadMessages,
      infiniteScrollDirection
    });
    const _loadNextPage = () => loadNextPage({
      loadedAllPrevMessages,
      internalListState: listState,
      pageRefs,
      updateListState,
      finishedScrollPositionUpdateRef,
      infiniteScrollDirection
    });
    return {
      actions: {
        loadNextPage: _isInfiniteScrollUp ? _loadNextPage : _loadPrevPage,
        loadPrevPage: _isInfiniteScrollUp ? _loadPrevPage : _loadNextPage
      },
      bottomPageId,
      loadedAllPrevMessages,
      pages,
      allLoadedPages: listState.loadedPages,
      loadedMessagesCount: listState.loadedMessagesCount,
      loadPrevPromiseRef
    };
  }, [
    _isInfiniteScrollUp,
    bottomPageId,
    loadedAllPrevMessages,
    pages,
    listState,
    setFinishedScrollPositionUpdate,
    setLastScrollPosition,
    tableRef,
    updateLogViewState,
    finishedScrollPositionUpdateRef,
    pageRefs,
    onLoadMessages,
    infiniteScrollDirection
  ]);
  return /* @__PURE__ */ React.createElement(ListStateContext.Provider, { value: listStateContextValue }, children);
};
export default ListStateProvider;
